/** ListField — label + add-one-at-a-time list input. */

import { type ReactNode, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

interface Props {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  isRequired?: boolean;
  errorMessage?: string;
  isInvalid?: boolean;
  contextualHelp?: ReactNode;
}

export default function ListField({
  label,
  value,
  onChange,
  placeholder,
  isRequired,
  errorMessage,
  isInvalid,
  contextualHelp,
}: Props) {
  const [draft, setDraft] = useState('');

  const addItem = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...value, trimmed]);
    setDraft('');
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-bold">
        {label}
        {isRequired && <span className="ms-1 text-destructive" aria-hidden>*</span>}
        {contextualHelp}
      </div>

      <div className="flex items-end gap-2">
        <div className="grow">
          <Input
            aria-label={`Add ${label}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addItem();
              }
            }}
            placeholder={placeholder}
          />
        </div>
        <Button
          aria-label={`Add to ${label}`}
          onClick={addItem}
          disabled={!draft.trim()}
          variant="outline"
          size="icon"
        >
          <Plus />
        </Button>
      </div>

      {value.length > 0 && (
        <ul aria-label={label} className="flex flex-col gap-1">
          {value.map((item, index) => (
            <li
              key={`${index}-${item}`}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm"
            >
              <span>{item}</span>
              <Button
                aria-label={`Remove ${item}`}
                onClick={() => removeItem(index)}
                variant="ghost"
                size="icon-sm"
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {isInvalid && errorMessage && (
        <p className="text-sm text-destructive">{errorMessage}</p>
      )}
    </div>
  );
}
